import React, { forwardRef } from 'react';
import { COMPANY_NAME_CN, COMPANY_NAME_EN } from './constants';
import { Cpu, Globe, Heart } from 'lucide-react';
import { useLanguage } from './i18n/LanguageContext';

export const AboutSection = forwardRef<HTMLDivElement>((props, ref) => {
  const { t } = useLanguage();

  const formatDescription = (text: string) => {
    const processed = text.replace('{company}', COMPANY_NAME_CN);
    const parts = processed.split(/<bold>|<\/bold>/g);
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        return <span key={i} className="text-white font-semibold">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <section ref={ref} className="bg-brand-black text-white py-24 md:py-32 relative overflow-hidden">
      
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-gray-900 to-transparent opacity-50 pointer-events-none" />
      
      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          
          {/* Text Content */}
          <div className="space-y-8">
            <div className="space-y-2">
              <h3 className="text-brand-gold text-sm tracking-[0.2em] uppercase font-bold">{t.about.title}</h3>
              <h2 className="text-4xl md:text-4xl font-display font-bold leading-tight">
                {t.about.brand} <br />
                <span className="text-gray-500 text-3xl md:text-3xl">{COMPANY_NAME_EN}</span>
              </h2>
            </div>
            
            <p className="text-gray-400 text-base leading-relaxed font-light">
              {formatDescription(t.about.description1)}
            </p>
            <p className="text-gray-400 text-base leading-relaxed font-light">
              {t.about.description2}
            </p>

            <div className="pt-8 grid grid-cols-1 sm:grid-cols-3 gap-8 border-t border-gray-800 mt-8">
              <div className="flex flex-col items-start space-y-3">
                <Cpu className="text-brand-gold w-8 h-8" />
                <h4 className="text-white font-semibold tracking-wide">{t.about.features.ai.title}</h4>
                <p className="text-xs text-gray-500">{t.about.features.ai.desc}</p>
              </div>
              <div className="flex flex-col items-start space-y-3">
                <Globe className="text-brand-gold w-8 h-8" />
                <h4 className="text-white font-semibold tracking-wide">{t.about.features.motion.title}</h4>
                <p className="text-xs text-gray-500">{t.about.features.motion.desc}</p>
              </div>
              <div className="flex flex-col items-start space-y-3">
                <Heart className="text-brand-gold w-8 h-8" />
                <h4 className="text-white font-semibold tracking-wide">{t.about.features.companion.title}</h4>
                <p className="text-xs text-gray-500">{t.about.features.companion.desc}</p>
              </div>
            </div>
          </div>

          <div className="relative h-[600px] bg-gray-900 rounded-sm overflow-hidden flex items-center justify-center group">
            <img 
               src="/images/about_us.jpg" 
               alt="about_us" 
               className="absolute inset-0 w-full h-full object-cover opacity-80  transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
            <div className="absolute bottom-8 left-8 border-l-2 border-brand-gold pl-4">
              <p className="text-gray-400 text-sm">{t.about.location}</p>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
});

AboutSection.displayName = "AboutSection";
